// Enhanced Debrief Export Service with HTML and DOCX support
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle, Table, TableRow, TableCell, WidthType, ImageRun } from 'docx';

const debriefExportService = {
  // Main export function
  async exportDebrief(debrief, format, projectName) {
    const timestamp = new Date().toISOString().split('T')[0];
    const baseFilename = `debrief-${projectName.replace(/\s+/g, '-')}-${timestamp}`;

    switch (format) {
      case 'markdown':
        return this.exportMarkdown(debrief, baseFilename);
      case 'html':
        return this.exportHTML(debrief, baseFilename);
      case 'docx':
        return this.exportDOCX(debrief, baseFilename);
      default:
        throw new Error(`Unsupported format: ${format}`);
    }
  },

  // Export as Markdown
  exportMarkdown(debrief, filename) {
    const content = this.generateMarkdownContent(debrief);
    const blob = new Blob([content], { type: 'text/markdown' });
    saveAs(blob, `${filename}.md`);
  },

  // Export as HTML with embedded images and professional styling
  async exportHTML(debrief, filename) {
    const htmlContent = await this.generateHTMLContent(debrief);
    const blob = new Blob([htmlContent], { type: 'text/html' });
    saveAs(blob, `${filename}.html`);
  },

  // Preview HTML in a new browser tab (for print-to-PDF)
  async previewHTML(debrief) {
    const htmlContent = await this.generateHTMLContent(debrief);
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  },

  // Export as DOCX with images and tables
  async exportDOCX(debrief, filename) {
    const doc = await this.generateDOCXDocument(debrief);
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${filename}.docx`);
  },

  // Generate comprehensive Markdown content
  generateMarkdownContent(debrief) {
    if (!debrief || !debrief.metadata) {
      throw new Error('Invalid debrief data');
    }
    let content = `# Red Team Engagement Debrief\n\n`;
    content += `**${debrief.metadata.classification || 'CONFIDENTIAL'}**\n\n`;

    // Metadata
    content += `## Project Information\n`;
    content += `- **Project:** ${debrief.metadata.projectName || 'N/A'}\n`;
    content += `- **Client:** ${debrief.metadata.client || 'N/A'}\n`;
    content += `- **Date Range:** ${debrief.metadata.startDate ? new Date(debrief.metadata.startDate).toLocaleDateString() : 'N/A'} - ${debrief.metadata.endDate ? new Date(debrief.metadata.endDate).toLocaleDateString() : 'N/A'}\n`;
    content += `- **Generated:** ${debrief.metadata.generatedDate ? new Date(debrief.metadata.generatedDate).toLocaleDateString() : new Date().toLocaleDateString()}\n\n`;

    // Table of Contents
    content += `## Table of Contents\n`;
    content += `1. [Executive Summary](#executive-summary)\n`;
    content += `2. [Engagement Timeline](#engagement-timeline)\n`;
    content += `3. [Statistics & Metrics](#statistics--metrics)\n`;
    content += `4. [Key Findings](#key-findings)\n`;
    content += `5. [Actions by Target](#actions-by-target)\n`;
    content += `6. [MITRE ATT&CK Analysis](#mitre-attck-analysis)\n`;
    content += `7. [Technical Details](#technical-details)\n`;
    content += `8. [Recommendations](#recommendations)\n`;
    content += `9. [Lessons Learned](#lessons-learned)\n`;
    content += `10. [Attack Path Visualization](#attack-path-visualization)\n\n`;

    // Executive Summary
    content += `## Executive Summary\n\n`;
    if (debrief.executiveSummary) {
      content += `### Overview\n${debrief.executiveSummary.overview || 'N/A'}\n\n`;
      if (debrief.executiveSummary.scope) {
        content += `### Engagement Scope\n${debrief.executiveSummary.scope}\n\n`;
      }
      content += `### Key Statistics\n`;
      if (debrief.executiveSummary.keyStatistics) {
        Object.entries(debrief.executiveSummary.keyStatistics).forEach(([key, value]) => {
          content += `- **${this.formatKey(key)}:** ${value}\n`;
        });
      }
      if (debrief.executiveSummary.mainFindings) {
        content += `\n### Main Findings\n${debrief.executiveSummary.mainFindings}\n\n`;
      }
      if (debrief.executiveSummary.overallRisk) {
        content += `### Overall Risk Assessment\n`;
        content += `**${debrief.executiveSummary.overallRisk.level}** - ${debrief.executiveSummary.overallRisk.description}\n\n`;
      }
    }

    // Timeline
    content += `## Engagement Timeline\n\n`;
    if (debrief.timeline && debrief.timeline.length > 0) {
      content += `| # | Timestamp | Action | Technique | Operator | Result | Duration | Critical |\n`;
      content += `|---|-----------|--------|-----------|----------|--------|----------|----------|\n`;
      debrief.timeline.forEach(event => {
        const timestamp = event.timestamp ? new Date(event.timestamp).toLocaleString() : 'N/A';
        const critical = event.critical ? 'Yes' : 'No';
        content += `| ${event.sequence || '-'} | ${timestamp} | ${event.action || '-'} | ${event.technique || '-'} | ${event.operator || '-'} | ${event.result || '-'} | ${event.duration || '-'} | ${critical} |\n`;
      });
      content += `\n`;
    } else {
      content += `No timeline events recorded.\n\n`;
    }

    // Statistics
    content += `## Statistics & Metrics\n\n`;
    content += `### Overall Performance\n`;
    content += `- **Total Actions:** ${debrief.statistics.totalActions}\n`;
    content += `- **Success Rate:** ${Math.round(debrief.statistics.successRate * 100)}%\n`;
    content += `- **Detection Rate:** ${Math.round(debrief.statistics.detectionRate * 100)}%\n`;
    content += `- **Targets Compromised:** ${debrief.statistics.targetsCompromised}/${debrief.statistics.totalTargets}\n`;
    content += `- **Unique Techniques:** ${debrief.statistics.uniqueTechniques}\n`;
    content += `- **Unique Tactics:** ${debrief.statistics.uniqueTactics}\n\n`;

    // Key Findings
    content += `## Key Findings\n\n`;
    if (debrief.findings) {
      ['critical', 'high', 'medium', 'low'].forEach(severity => {
        const findings = debrief.findings[severity];
        if (findings && findings.length > 0) {
          content += `### ${severity.charAt(0).toUpperCase() + severity.slice(1)} Findings\n`;
          findings.forEach((finding, i) => {
            content += `\n#### ${i + 1}. ${finding.title}\n`;
            content += `- **Severity:** ${severity.charAt(0).toUpperCase() + severity.slice(1)}\n`;
            content += `- **Description:** ${finding.description || 'N/A'}\n`;
            if (finding.impact) content += `- **Impact:** ${finding.impact}\n`;
            if (finding.affectedAssets && finding.affectedAssets.length > 0) {
              content += `- **Affected Assets:** ${finding.affectedAssets.join(', ')}\n`;
            }
            if (finding.remediation) content += `- **Remediation:** ${finding.remediation}\n`;
          });
        }
      });
    }

    // Actions by Target
    content += `\n## Actions by Target\n\n`;
    if (debrief.actionsByTarget) {
      Object.entries(debrief.actionsByTarget).forEach(([target, data]) => {
        content += `### ${target}\n`;
        content += `- **Type:** ${data.type}\n`;
        content += `- **Description:** ${data.description || 'N/A'}\n`;
        content += `- **Total Actions:** ${data.actions.length}\n`;
        content += `- **Successful:** ${data.successful}\n`;
        content += `- **Detected:** ${data.detected}\n`;
        content += `- **Blocked:** ${data.blocked}\n`;
        content += `- **Failed:** ${data.failed || 0}\n`;
        content += `- **Techniques Used:** ${data.techniques instanceof Set ? data.techniques.size : (typeof data.techniques === 'object' ? Object.keys(data.techniques).length : data.techniques || 0)}\n\n`;
        
        content += `#### Actions Timeline\n`;
        data.actions.forEach((action, i) => {
          content += `${i + 1}. **${action.title}** (${new Date(action.timestamp).toLocaleString()})\n`;
          content += `   - Result: ${action.result}\n`;
          content += `   - Technique: ${action.technique || 'N/A'}\n`;
          content += `   - Operator: ${action.operator}\n\n`;
        });
      });
    }

    // MITRE ATT&CK Analysis
    content += `## MITRE ATT&CK Analysis\n\n`;
    content += `### Techniques by Severity and Result\n\n`;
    if (debrief.mitreAnalysis) {
      debrief.mitreAnalysis.forEach(item => {
        content += `#### ${item.technique} - ${item.name}\n`;
        content += `- **Tactic:** ${item.tactic}\n`;
        content += `- **Severity:** ${item.severity}\n`;
        content += `- **Total Attempts:** ${item.attempts}\n`;
        content += `- **Success Rate:** ${Math.round(item.successRate * 100)}%\n`;
        content += `- **Actions:**\n`;
        item.actions.forEach(action => {
          content += `  - ${action.title} (${action.result})\n`;
        });
        content += `\n`;
      });
    }

    // Add other sections...
    return content;
  },

  // Generate professional HTML content
  async generateHTMLContent(debrief) {
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Red Team Debrief - ${debrief.metadata.projectName}</title>
    <style>
        ${this.getHTMLStyles()}
    </style>
</head>
<body class="has-print-bar">
    <div class="print-bar" id="printBar">
        <span>Red Team Debrief &mdash; ${debrief.metadata.projectName}</span>
        <button onclick="window.print()">Print / Save as PDF</button>
    </div>
    <div class="container">
        <header>
            <h1>Red Team Engagement Debrief</h1>
            <div class="classification">${debrief.metadata.classification}</div>
            <div class="metadata">
                <div class="metadata-item">
                    <strong>Project:</strong> ${debrief.metadata.projectName}
                </div>
                <div class="metadata-item">
                    <strong>Client:</strong> ${debrief.metadata.client}
                </div>
                <div class="metadata-item">
                    <strong>Date Range:</strong> ${new Date(debrief.metadata.startDate).toLocaleDateString()} - ${new Date(debrief.metadata.endDate).toLocaleDateString()}
                </div>
                <div class="metadata-item">
                    <strong>Generated:</strong> ${new Date(debrief.metadata.generatedDate).toLocaleDateString()}
                </div>
            </div>
        </header>

        <nav class="toc">
            <h2>Table of Contents</h2>
            <ol>
                <li><a href="#executive-summary">Executive Summary</a></li>
                <li><a href="#timeline">Engagement Timeline</a></li>
                <li><a href="#statistics">Statistics & Metrics</a></li>
                <li><a href="#findings">Key Findings</a></li>
                <li><a href="#actions-by-target">Actions by Target</a></li>
                <li><a href="#mitre-analysis">MITRE ATT&CK Analysis</a></li>
                ${debrief.webAssessment ? '<li><a href="#web-assessment">Web Application Security Assessment</a></li>' : ''}
                <li><a href="#attack-path">Attack Path Visualization</a></li>
                <li><a href="#action-details">Detailed Action Log</a></li>
                <li><a href="#recommendations">Recommendations</a></li>
            </ol>
        </nav>

        <section id="executive-summary" class="section">
            <h2>Executive Summary</h2>
            <div class="subsection">
                <h3>Overview</h3>
                <p>${debrief.executiveSummary?.overview || 'No overview available.'}</p>
            </div>
            <div class="subsection">
                <h3>Key Statistics</h3>
                <div class="stats-grid">
                    ${debrief.executiveSummary?.keyStatistics ? Object.entries(debrief.executiveSummary.keyStatistics).map(([key, value]) => `
                        <div class="stat-card">
                            <div class="stat-value">${value}</div>
                            <div class="stat-label">${this.formatKey(key)}</div>
                        </div>
                    `).join('') : '<p>No statistics available.</p>'}
                </div>

                <!-- Severity Distribution Bar Chart -->
                <div style="margin-top: 2rem;">
                    <h4 style="margin-bottom: 1rem;">Finding Severity Distribution</h4>
                    ${this.generateSeverityBarChart(debrief.findings || {})}
                </div>

                <!-- Action Results by Target Chart -->
                ${debrief.actionsByTarget ? `
                <div style="margin-top: 2rem;">
                    <h4 style="margin-bottom: 1rem;">Action Results by Target</h4>
                    ${this.generateTargetResultsChart(debrief.actionsByTarget)}
                </div>
                ` : ''}
            </div>
            ${debrief.executiveSummary?.overallRisk ? `
            <div class="risk-assessment ${(debrief.executiveSummary.overallRisk.level || 'medium').toLowerCase()}">
                <h3>Overall Risk Assessment</h3>
                <div class="risk-level">${debrief.executiveSummary.overallRisk.level}</div>
                <p>${debrief.executiveSummary.overallRisk.description}</p>
            </div>
            ` : ''}
        </section>

        <section id="timeline" class="section">
            <h2>Engagement Timeline</h2>
            <div class="timeline-container">
                ${debrief.timeline ? this.generateEnhancedTimelineHTML(debrief.timeline) : '<p>No timeline data available.</p>'}
            </div>
        </section>

        <section id="findings" class="section">
            <h2>Key Findings</h2>
            ${debrief.findings ? this.generateFindingsHTML(debrief.findings) : '<p>No findings available.</p>'}
        </section>

        <section id="actions-by-target" class="section">
            <h2>Actions by Target</h2>
            ${this.generateEnhancedActionsByTargetHTML(debrief.actionsByTarget)}
        </section>

        <section id="mitre-analysis" class="section">
            <h2>MITRE ATT&CK Analysis</h2>
            ${this.generateFullMitreMapHTML(debrief.mitreAnalysis, debrief.statistics)}
            <div style="margin-top: 2rem;">
                ${this.generateMitreAnalysisHTML(debrief.mitreAnalysis)}
            </div>
        </section>

        ${debrief.webAssessment ? `
        <section id="web-assessment" class="section">
            <h2>Web Application Security Assessment</h2>
            ${this.generateWebAssessmentHTML(debrief.webAssessment)}
        </section>
        ` : ''}

        <section id="attack-path" class="section">
            <h2>Attack Path Visualization</h2>
            ${this.generateEnhancedAttackPathHTML(debrief.attackPath)}
        </section>

        <section id="action-details" class="section">
            <h2>Detailed Action Log</h2>
            ${this.generateDetailedActionsHTML(debrief.detailedActions)}
        </section>

        <section id="recommendations" class="section">
            <h2>Recommendations</h2>
            ${debrief.recommendations ? this.generateRecommendationsHTML(debrief.recommendations) : '<p>No recommendations available.</p>'}
        </section>
    </div>
</body>
</html>`;
    
    return html;
  },

  // Generate DOCX document
  async generateDOCXDocument(debrief) {
    const sections = [];

    // Title Page
    sections.push(
      new Paragraph({
        text: 'Red Team Engagement Debrief',
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 }
      }),
      new Paragraph({
        text: debrief.metadata.classification,
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        style: 'classification'
      }),
      new Paragraph({
        text: `Project: ${debrief.metadata.projectName}`,
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 }
      }),
      new Paragraph({
        text: `Client: ${debrief.metadata.client}`,
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 }
      }),
      new Paragraph({
        text: `Generated: ${new Date(debrief.metadata.generatedDate).toLocaleDateString()}`,
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 }
      }),
      new Paragraph({ children: [new TextRun({ break: 1 })], pageBreakBefore: true })
    );

    // Executive Summary
    sections.push(
      new Paragraph({
        text: 'Executive Summary',
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 200 }
      }),
      new Paragraph({
        text: 'Overview',
        heading: HeadingLevel.HEADING_2,
        spacing: { after: 100 }
      }),
      new Paragraph({
        text: debrief.executiveSummary.overview,
        spacing: { after: 200 }
      })
    );

    // Key Statistics Table
    const statsTable = new Table({
      rows: [
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({ text: 'Metric', bold: true })],
              width: { size: 50, type: WidthType.PERCENTAGE }
            }),
            new TableCell({
              children: [new Paragraph({ text: 'Value', bold: true })],
              width: { size: 50, type: WidthType.PERCENTAGE }
            })
          ]
        }),
        ...Object.entries(debrief.executiveSummary.keyStatistics).map(([key, value]) => 
          new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph({ text: this.formatKey(key) })]
              }),
              new TableCell({
                children: [new Paragraph({ text: String(value) })]
              })
            ]
          })
        )
      ],
      width: { size: 100, type: WidthType.PERCENTAGE }
    });

    sections.push(
      new Paragraph({
        text: 'Key Statistics',
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      }),
      statsTable
    );

    // Add Timeline section
    sections.push(
      new Paragraph({ children: [new TextRun({ break: 1 })], pageBreakBefore: true }),
      new Paragraph({
        text: 'Engagement Timeline',
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 200 }
      })
    );

    // Create timeline table
    const timelineTable = this.createEnhancedTimelineTable(debrief.timeline);
    sections.push(timelineTable);

    // Add Actions by Target
    if (debrief.actionsByTarget) {
      sections.push(
        new Paragraph({ children: [new TextRun({ break: 1 })], pageBreakBefore: true }),
        new Paragraph({
          text: 'Actions by Target',
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 200 }
        })
      );

      Object.entries(debrief.actionsByTarget).forEach(([target, data]) => {
        sections.push(
          new Paragraph({
            text: `${target}`,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 }
          }),
          new Paragraph({
            text: `Type: ${data.type} | Description: ${data.description || 'N/A'}`,
            spacing: { after: 50 }
          }),
          new Paragraph({
            text: `Total Actions: ${data.actions.length} | Successful: ${data.successful} | Detected: ${data.detected} | Blocked: ${data.blocked} | Failed: ${data.failed || 0}`,
            spacing: { after: 100 }
          })
        );

        // Add action details for this target
        data.actions.forEach((action, i) => {
          sections.push(
            new Paragraph({
              text: `${i + 1}. ${action.title}`,
              bold: true,
              spacing: { before: 100, after: 50 }
            }),
            new Paragraph({
              text: `Result: ${action.result} | Technique: ${action.technique || 'N/A'} | Time: ${new Date(action.timestamp).toLocaleString()}`,
              spacing: { after: 50 }
            })
          );
        });
      });
    }

    // Add Web Assessment section (conditional)
    if (debrief.webAssessment) {
      sections.push(
        new Paragraph({ children: [new TextRun({ break: 1 })], pageBreakBefore: true }),
        new Paragraph({
          text: 'Web Application Security Assessment',
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 200 }
        })
      );

      // Targets overview
      sections.push(
        new Paragraph({
          text: 'Web Targets Tested',
          heading: HeadingLevel.HEADING_2,
          spacing: { after: 100 }
        })
      );
      debrief.webAssessment.targets.forEach(t => {
        sections.push(
          new Paragraph({
            text: `${t.baseUrl} (${t.webType || 'web application'})`,
            spacing: { after: 50 }
          })
        );
      });

      // Severity breakdown
      const wa = debrief.webAssessment;
      sections.push(
        new Paragraph({
          text: 'Findings Summary',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          text: `Total Findings: ${wa.totalFindings} | Critical: ${wa.severityBreakdown.critical} | High: ${wa.severityBreakdown.high} | Medium: ${wa.severityBreakdown.medium} | Low: ${wa.severityBreakdown.low}`,
          spacing: { after: 100 }
        })
      );

      // Web findings table
      if (wa.findings.length > 0) {
        const webFindingsTable = new Table({
          rows: [
            new TableRow({
              children: ['Title', 'Severity', 'WSTG Test', 'Endpoint'].map(h =>
                new TableCell({
                  children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })],
                  width: { size: 25, type: WidthType.PERCENTAGE }
                })
              )
            }),
            ...wa.findings.map(f =>
              new TableRow({
                children: [
                  new TableCell({ children: [new Paragraph({ text: f.title || '' })] }),
                  new TableCell({ children: [new Paragraph({ text: (f.severity || '').toUpperCase() })] }),
                  new TableCell({ children: [new Paragraph({ text: f.wstgTestId || '' })] }),
                  new TableCell({ children: [new Paragraph({ text: f.endpoint || '' })] })
                ]
              })
            )
          ],
          width: { size: 100, type: WidthType.PERCENTAGE }
        });
        sections.push(webFindingsTable);
      }
    }

    // Add Evidence/Screenshots section
    sections.push(
      new Paragraph({ children: [new TextRun({ break: 1 })], pageBreakBefore: true }),
      new Paragraph({
        text: 'Evidence and Screenshots',
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 200 }
      })
    );

    // Process and add evidence
    if (debrief.detailedActions) {
      for (const action of debrief.detailedActions) {
        if (action.evidence && action.evidence.length > 0) {
          sections.push(
            new Paragraph({
              text: action.title,
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 }
            })
          );

          for (const evidence of action.evidence) {
            if (evidence.type === 'screenshot' && evidence.data) {
              try {
                // Convert base64 to Uint8Array for DOCX (browser-compatible)
                const base64Data = evidence.data.includes(',') ? evidence.data.split(',')[1] : evidence.data;
                const binaryString = atob(base64Data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                sections.push(
                  new Paragraph({
                    children: [
                      new ImageRun({
                        data: bytes,
                        transformation: {
                          width: 600,
                          height: 400
                        }
                      })
                    ],
                    spacing: { after: 100 }
                  }),
                  new Paragraph({
                    text: evidence.caption || 'Screenshot evidence',
                    alignment: AlignmentType.CENTER,
                    spacing: { after: 200 }
                  })
                );
              } catch (error) {
                console.error('Error processing screenshot:', error);
              }
            } else if (evidence.type === 'url') {
              sections.push(
                new Paragraph({
                  text: `Evidence URL: ${evidence.url}`,
                  spacing: { after: 50 }
                })
              );
            }
          }
        }
      }
    }

    // Create the document
    const doc = new Document({
      sections: [{
        properties: {},
        children: sections
      }],
      styles: {
        paragraphStyles: [
          {
            id: "classification",
            name: "Classification",
            basedOn: "Normal",
            run: {
              color: "FF0000",
              bold: true,
              size: 28
            }
          }
        ]
      }
    });

    return doc;
  },

  // Helper methods
  getHTMLStyles() {
    return `
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        line-height: 1.6;
        color: #333;
        margin: 0;
        padding: 0;
        background: #f5f5f5;
      }
      .container {
        max-width: 1200px;
        margin: 0 auto;
        background: white;
        box-shadow: 0 0 20px rgba(0,0,0,0.1);
      }
      header {
        background: #1a202c;
        color: white;
        padding: 2rem;
        text-align: center;
      }
      h1 {
        margin: 0;
        font-size: 2.5rem;
      }
      .classification {
        background: #dc2626;
        color: white;
        padding: 0.5rem 1rem;
        display: inline-block;
        margin: 1rem 0;
        font-weight: bold;
        border-radius: 4px;
      }
      .metadata {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 1rem;
        margin-top: 2rem;
      }
      .metadata-item {
        background: rgba(255,255,255,0.1);
        padding: 0.5rem 1rem;
        border-radius: 4px;
      }
      .toc {
        background: #f8f9fa;
        padding: 2rem;
        border-bottom: 1px solid #e9ecef;
      }
      .toc h2 {
        margin-top: 0;
      }
      .toc ol {
        list-style: none;
        padding: 0;
      }
      .toc li {
        margin: 0.5rem 0;
      }
      .toc a {
        color: #2563eb;
        text-decoration: none;
      }
      .toc a:hover {
        text-decoration: underline;
      }
      .section {
        padding: 3rem 2rem;
        border-bottom: 1px solid #e9ecef;
      }
      .stats-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 1rem;
        margin: 2rem 0;
      }
      .stat-card {
        background: #f3f4f6;
        padding: 1.5rem;
        border-radius: 8px;
        text-align: center;
      }
      .stat-value {
        font-size: 2rem;
        font-weight: bold;
        color: #1f2937;
      }
      .stat-label {
        color: #6b7280;
        margin-top: 0.5rem;
      }
      .risk-assessment {
        padding: 1.5rem;
        border-radius: 8px;
        margin: 2rem 0;
      }
      .risk-assessment.critical {
        background: #fee2e2;
        border: 2px solid #dc2626;
      }
      .risk-assessment.high {
        background: #fed7aa;
        border: 2px solid #ea580c;
      }
      .risk-assessment.medium {
        background: #fef3c7;
        border: 2px solid #f59e0b;
      }
      .risk-level {
        font-size: 1.5rem;
        font-weight: bold;
        margin-bottom: 0.5rem;
      }
      .timeline-container {
        position: relative;
        padding: 2rem 0;
      }
      .timeline-event {
        position: relative;
        padding: 1rem 2rem;
        margin: 1rem 0;
        background: #f9fafb;
        border-left: 4px solid #3b82f6;
        border-radius: 4px;
      }
      .timeline-event.critical {
        border-left-color: #dc2626;
        background: #fee2e2;
      }
      .timeline-event.successful {
        border-left-color: #10b981;
      }
      .finding-card {
        background: #f9fafb;
        padding: 1.5rem;
        margin: 1rem 0;
        border-radius: 8px;
        border: 1px solid #e5e7eb;
      }
      .finding-card.critical {
        background: #fee2e2;
        border-color: #dc2626;
      }
      .finding-card.high {
        background: #fed7aa;
        border-color: #ea580c;
      }
      .target-section {
        background: #f9fafb;
        padding: 1.5rem;
        margin: 1rem 0;
        border-radius: 8px;
      }
      .action-detail {
        background: white;
        padding: 1rem;
        margin: 0.5rem 0;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
      }
      .mitre-technique {
        background: #f3e8ff;
        padding: 1.5rem;
        margin: 1rem 0;
        border-radius: 8px;
        border: 1px solid #c084fc;
      }
      .mitre-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 0.5rem;
        margin: 1rem 0;
      }
      .mitre-tactic {
        background: #f3f4f6;
        padding: 1rem;
        border-radius: 4px;
        text-align: center;
        font-weight: 600;
        font-size: 0.875rem;
      }
      .mitre-tactic.executed {
        background: #c084fc;
        color: white;
      }
      .mitre-tactic.not-executed {
        background: #e5e7eb;
        color: #6b7280;
      }
      .attack-flow {
        background: #f9fafb;
        padding: 1.5rem;
        border-radius: 8px;
        margin: 1rem 0;
      }
      .flow-node {
        background: white;
        border: 2px solid #3b82f6;
        padding: 1rem;
        border-radius: 8px;
        margin: 0.5rem 0;
        position: relative;
      }
      .flow-node.critical {
        border-color: #dc2626;
        background: #fee2e2;
      }
      .flow-arrow {
        width: 0;
        height: 0;
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-top: 20px solid #3b82f6;
        margin: 0 auto;
      }
      .evidence-container {
        margin: 1rem 0;
      }
      .evidence-screenshot {
        max-width: 100%;
        height: auto;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        margin: 0.5rem 0;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 1rem 0;
      }
      th, td {
        border: 1px solid #e5e7eb;
        padding: 0.75rem;
        text-align: left;
      }
      th {
        background: #f3f4f6;
        font-weight: bold;
      }
      .recommendation {
        background: #ecfdf5;
        padding: 1.5rem;
        margin: 1rem 0;
        border-radius: 8px;
        border: 1px solid #10b981;
      }
      .print-bar {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        background: #1a202c;
        color: white;
        padding: 0.75rem 2rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        z-index: 1000;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      }
      .print-bar button {
        background: #2563eb;
        color: white;
        border: none;
        padding: 0.5rem 1.5rem;
        border-radius: 4px;
        font-size: 0.875rem;
        cursor: pointer;
        font-weight: 600;
      }
      .print-bar button:hover {
        background: #1d4ed8;
      }
      body.has-print-bar {
        padding-top: 60px;
      }
      @media print {
        .print-bar {
          display: none !important;
        }
        body.has-print-bar {
          padding-top: 0;
        }
        body {
          background: white;
          color: black;
          font-size: 10pt;
          line-height: 1.4;
        }
        .container {
          box-shadow: none;
          max-width: 100%;
        }
        header {
          background: white !important;
          color: black !important;
          border-bottom: 3px solid #1a202c;
        }
        header h1 {
          color: #1a202c;
        }
        .metadata-item {
          background: #f3f4f6 !important;
          color: black;
        }
        .toc {
          page-break-after: always;
        }
        .section {
          page-break-inside: avoid;
          padding: 1.5rem 0;
          border-bottom: 1px solid #ccc;
        }
        .stats-grid {
          grid-template-columns: repeat(3, 1fr);
        }
        .stat-card {
          border: 1px solid #ccc;
        }
        .timeline-event {
          page-break-inside: avoid;
        }
        .finding-card {
          page-break-inside: avoid;
        }
        .target-section {
          page-break-inside: avoid;
        }
        .recommendation {
          page-break-inside: avoid;
        }
        a {
          color: black;
          text-decoration: none;
        }
        .evidence-screenshot {
          max-width: 80%;
          page-break-inside: avoid;
        }
        h2 {
          page-break-after: avoid;
        }
        h3 {
          page-break-after: avoid;
        }
      }
    `;
  },

  generateEnhancedTimelineHTML(timeline) {
    return timeline.map(event => {
      const timestamp = new Date(event.timestamp).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      const endTime = event.endTime ? new Date(event.endTime).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';
      const isCritical = event.critical;
      
      return `
        <div class="timeline-event ${isCritical ? 'critical' : ''} ${event.result}">
          <div style="display: flex; justify-content: space-between; align-items: start;">
            <div style="flex: 1;">
              <h4 style="margin: 0 0 0.5rem 0;">${event.action}</h4>
              <p style="margin: 0.25rem 0;"><strong>Start:</strong> ${timestamp}</p>
              ${event.endTime ? `<p style="margin: 0.25rem 0;"><strong>End:</strong> ${endTime}</p>` : ''}
              <p style="margin: 0.25rem 0;"><strong>Duration:</strong> ${event.duration}</p>
              <p style="margin: 0.25rem 0;"><strong>Technique:</strong> ${event.technique}</p>
              <p style="margin: 0.25rem 0;"><strong>Operator:</strong> ${event.operator}</p>
              ${isCritical ? '<p style="color: #dc2626; font-weight: bold; margin: 0.5rem 0;">⚠️ CRITICAL ACTION</p>' : ''}
            </div>
            <div>
              <span class="badge ${event.result}" style="background: ${
                event.result === 'successful' ? '#10b981' :
                event.result === 'detected' ? '#f59e0b' :
                event.result === 'blocked' ? '#ef4444' :
                '#6b7280'
              }; color: white; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.875rem;">${event.result.toUpperCase()}</span>
            </div>
          </div>
        </div>
      `;
    }).join('');
  },

  generateFindingsHTML(findings) {
    if (!findings) return '<p>No findings data available.</p>';
    let html = '';

    const severityLevels = [
      { key: 'critical', label: 'Critical Findings', cssClass: 'critical' },
      { key: 'high', label: 'High Severity Findings', cssClass: 'high' },
      { key: 'medium', label: 'Medium Severity Findings', cssClass: '' },
      { key: 'low', label: 'Low Severity Findings', cssClass: '' }
    ];

    severityLevels.forEach(({ key, label, cssClass }) => {
      const items = findings[key];
      if (items && items.length > 0) {
        html += `<h3>${label}</h3>`;
        items.forEach(finding => {
          html += `
            <div class="finding-card ${cssClass}">
              <h4>${finding.title || 'Untitled Finding'}</h4>
              ${finding.impact ? `<p><strong>Impact:</strong> ${finding.impact}</p>` : ''}
              <p>${finding.description || 'No description'}</p>
              ${finding.affectedAssets && finding.affectedAssets.length > 0 ? `<p><strong>Affected Assets:</strong> ${finding.affectedAssets.join(', ')}</p>` : ''}
              ${finding.remediation ? `<p><strong>Remediation:</strong> ${finding.remediation}</p>` : ''}
            </div>
          `;
        });
      }
    });

    return html || '<p>No findings recorded.</p>';
  },

  generateEnhancedActionsByTargetHTML(actionsByTarget) {
    if (!actionsByTarget) return '<p>No target-specific data available.</p>';
    
    // Filter out empty targets (those with no actions)
    const targetsWithActions = Object.entries(actionsByTarget).filter(([_, data]) => 
      data.actions && data.actions.length > 0
    );
    
    if (targetsWithActions.length === 0) {
      // Show all targets even if no actions
      return Object.entries(actionsByTarget).map(([targetKey, data]) => {
        return `
          <div class="target-section">
            <h3>${data.value || targetKey} <span style="color: #6b7280; font-size: 0.875rem;">(${data.type})</span></h3>
            <p style="color: #666; margin-top: -10px;">${data.description || 'No description'}</p>
            <div class="stats-grid" style="margin-bottom: 1rem;">
              <div class="stat-card">
                <div class="stat-value">0</div>
                <div class="stat-label">Total Actions</div>
              </div>
              <div class="stat-card">
                <div class="stat-value" style="color: #10b981;">0</div>
                <div class="stat-label">Successful</div>
              </div>
              <div class="stat-card">
                <div class="stat-value" style="color: #f59e0b;">0</div>
                <div class="stat-label">Detected</div>
              </div>
              <div class="stat-card">
                <div class="stat-value" style="color: #ef4444;">0</div>
                <div class="stat-label">Blocked</div>
              </div>
              <div class="stat-card">
                <div class="stat-value" style="color: #6b7280;">0</div>
                <div class="stat-label">Failed</div>
              </div>
              <div class="stat-card">
                <div class="stat-value" style="color: #7c3aed;">0</div>
                <div class="stat-label">Unique Techniques</div>
              </div>
            </div>
            <p style="color: #6b7280; background: #f3f4f6; padding: 1rem; border-radius: 8px; text-align: center;">
              No actions recorded for this target. Actions may not be properly linked to targets.
            </p>
          </div>
        `;
      }).join('');
    }
    
    // Show targets with actions
    return targetsWithActions.map(([targetKey, data]) => {
      // Calculate real values from the data object
      const totalActions = data.actions ? data.actions.length : 0;
      const successfulCount = data.successful || 0;
      const detectedCount = data.detected || 0;
      const blockedCount = data.blocked || 0;
      const failedCount = data.failed || 0;
      const partialCount = data.partial || 0;
      const unknownCount = data.unknown || 0;
      const techniqueCount = data.techniques ? (data.techniques instanceof Set ? data.techniques.size : (typeof data.techniques === 'object' ? Object.keys(data.techniques).length : 0)) : 0;
      
      return `
        <div class="target-section">
          <h3>${data.value || targetKey} <span style="color: #6b7280; font-size: 0.875rem;">(${data.type})</span></h3>
          <p style="color: #666; margin-top: -10px;">${data.description || 'No description'}</p>
          <div class="stats-grid" style="margin-bottom: 1rem;">
            <div class="stat-card">
              <div class="stat-value">${totalActions}</div>
              <div class="stat-label">Total Actions</div>
            </div>
            <div class="stat-card">
              <div class="stat-value" style="color: #10b981;">${successfulCount}</div>
              <div class="stat-label">Successful</div>
            </div>
            <div class="stat-card">
              <div class="stat-value" style="color: #f59e0b;">${detectedCount}</div>
              <div class="stat-label">Detected</div>
            </div>
            <div class="stat-card">
              <div class="stat-value" style="color: #ef4444;">${blockedCount}</div>
              <div class="stat-label">Blocked</div>
            </div>
            <div class="stat-card">
              <div class="stat-value" style="color: #6b7280;">${failedCount}</div>
              <div class="stat-label">Failed</div>
            </div>
            <div class="stat-card">
              <div class="stat-value" style="color: #7c3aed;">${techniqueCount}</div>
              <div class="stat-label">Unique Techniques</div>
            </div>
          </div>
          ${partialCount > 0 || unknownCount > 0 ? `
            <div style="margin-bottom: 1rem; padding: 0.5rem; background: #f3f4f6; border-radius: 4px; font-size: 0.875rem;">
              ${partialCount > 0 ? `<span style="margin-right: 1rem;">Partial: ${partialCount}</span>` : ''}
              ${unknownCount > 0 ? `<span>Unknown: ${unknownCount}</span>` : ''}
            </div>
          ` : ''}
          ${data.firstCompromise ? `<p style="color: #dc2626; font-weight: bold;">First Compromised: ${new Date(data.firstCompromise).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>` : ''}
          <h4>Action Timeline</h4>
          ${data.actions && data.actions.length > 0 ? data.actions.map((action, i) => `
            <div class="action-detail" style="margin-bottom: 1rem; padding: 1rem; background: #f9fafb; border-left: 3px solid ${
              action.result === 'successful' ? '#10b981' :
              action.result === 'detected' ? '#f59e0b' :
              action.result === 'blocked' ? '#ef4444' :
              '#6b7280'
            };">
              <strong style="font-size: 1.1em;">${i + 1}. ${action.title}</strong>
              ${action.description ? `<p style="color: #4b5563; margin: 0.5rem 0;">${action.description}</p>` : ''}
              <div style="display: flex; gap: 1rem; margin-top: 0.5rem; flex-wrap: wrap;">
                <span style="color: #6b7280;"><strong>Time:</strong> ${new Date(action.timestamp).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                <span style="color: #6b7280;"><strong>Operator:</strong> ${action.operator}</span>
                <span style="color: ${
                  action.result === 'successful' ? '#10b981' :
                  action.result === 'detected' ? '#f59e0b' :
                  action.result === 'blocked' ? '#ef4444' :
                  '#6b7280'
                };"><strong>Result:</strong> ${action.result}</span>
                ${action.technique ? `<span style="color: #7c3aed;"><strong>Technique:</strong> ${action.technique}</span>` : ''}
              </div>
              ${action.notes ? `<div style="margin-top: 0.5rem; padding: 0.5rem; background: #e5e7eb; border-radius: 4px;"><strong>Notes:</strong> ${action.notes}</div>` : ''}
              ${action.evidence && action.evidence.length > 0 ? `<div style="margin-top: 0.5rem;"><strong>Evidence:</strong> ${action.evidence.length} item(s)</div>` : ''}
            </div>
          `).join('') : '<p style="color: #6b7280;">No actions recorded for this target.</p>'}
        </div>
      `;
    }).join('');
  },

  generateFullMitreMapHTML(mitreAnalysis, statistics) {
    const allTactics = [
      'Reconnaissance',
      'Resource Development',
      'Initial Access',
      'Execution',
      'Persistence',
      'Privilege Escalation',
      'Defense Evasion',
      'Credential Access',
      'Discovery',
      'Lateral Movement',
      'Collection',
      'Command and Control',
      'Exfiltration',
      'Impact'
    ];
    
    const executedTactics = new Set();
    if (mitreAnalysis) {
      mitreAnalysis.forEach(item => {
        if (item.tactic) {
          executedTactics.add(item.tactic);
        }
      });
    }
    
    return `
      <div>
        <h3>MITRE ATT&CK Framework Coverage</h3>
        <p style="margin-bottom: 1rem;">Executed tactics are highlighted in purple. Grey tactics were not executed during this engagement.</p>
        <div class="mitre-grid">
          ${allTactics.map(tactic => {
            const isExecuted = executedTactics.has(tactic);
            return `
              <div class="mitre-tactic ${isExecuted ? 'executed' : 'not-executed'}">
                ${tactic}
                ${isExecuted ? '<div style="font-size: 0.75rem; margin-top: 0.25rem;">✓ Executed</div>' : ''}
              </div>
            `;
          }).join('')}
        </div>
        <div style="margin-top: 1rem; text-align: center;">
          <p style="color: #6b7280;">
            Coverage: ${executedTactics.size} of ${allTactics.length} tactics (${Math.round((executedTactics.size / allTactics.length) * 100)}%)
          </p>
        </div>
      </div>
    `;
  },

  generateMitreAnalysisHTML(mitreAnalysis) {
    if (!mitreAnalysis) return '<p>No MITRE ATT&CK analysis available.</p>';
    
    return mitreAnalysis.map(item => `
      <div class="mitre-technique">
        <h3>${item.technique} - ${item.name}</h3>
        <p><strong>Tactic:</strong> ${item.tactic} | <strong>Severity:</strong> ${item.severity}</p>
        <div class="stats-grid" style="margin: 1rem 0;">
          <div class="stat-card">
            <div class="stat-value">${item.attempts}</div>
            <div class="stat-label">Attempts</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${Math.round(item.successRate * 100)}%</div>
            <div class="stat-label">Success Rate</div>
          </div>
        </div>
        <h4>Related Actions:</h4>
        ${item.actions.map(action => `
          <div class="action-detail">
            <strong>${action.title}</strong> - ${action.result}
          </div>
        `).join('')}
      </div>
    `).join('');
  },

  generateWebAssessmentHTML(webAssessment) {
    if (!webAssessment) return '';

    const sevColors = { critical: '#dc2626', high: '#ea580c', medium: '#f59e0b', low: '#22c55e', info: '#6b7280' };
    const sb = webAssessment.severityBreakdown;

    let html = `
      <div class="subsection">
        <h3>Web Targets Tested</h3>
        <div style="display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.5rem;">
          ${webAssessment.targets.map(t => `
            <div style="background: #f0fdfa; border: 1px solid #99f6e4; border-radius: 8px; padding: 0.75rem 1.25rem;">
              <strong style="color: #0d9488;">${t.baseUrl}</strong>
              <span style="color: #6b7280; margin-left: 0.5rem; font-size: 0.85rem;">${t.webType || 'web app'}</span>
            </div>
          `).join('')}
        </div>
      </div>

      <div class="subsection">
        <h3>Findings Summary</h3>
        <div class="stats-grid" style="margin-bottom: 1.5rem;">
          <div class="stat-card"><div class="stat-value">${webAssessment.totalFindings}</div><div class="stat-label">Total Findings</div></div>
          <div class="stat-card" style="border-left: 4px solid ${sevColors.critical};"><div class="stat-value" style="color: ${sevColors.critical};">${sb.critical}</div><div class="stat-label">Critical</div></div>
          <div class="stat-card" style="border-left: 4px solid ${sevColors.high};"><div class="stat-value" style="color: ${sevColors.high};">${sb.high}</div><div class="stat-label">High</div></div>
          <div class="stat-card" style="border-left: 4px solid ${sevColors.medium};"><div class="stat-value" style="color: ${sevColors.medium};">${sb.medium}</div><div class="stat-label">Medium</div></div>
          <div class="stat-card" style="border-left: 4px solid ${sevColors.low};"><div class="stat-value" style="color: ${sevColors.low};">${sb.low}</div><div class="stat-label">Low</div></div>
        </div>

        <!-- Severity bar -->
        <div style="display: flex; height: 24px; border-radius: 6px; overflow: hidden; margin-bottom: 1.5rem;">
          ${sb.critical > 0 ? `<div style="width: ${(sb.critical / webAssessment.totalFindings) * 100}%; background: ${sevColors.critical};" title="Critical: ${sb.critical}"></div>` : ''}
          ${sb.high > 0 ? `<div style="width: ${(sb.high / webAssessment.totalFindings) * 100}%; background: ${sevColors.high};" title="High: ${sb.high}"></div>` : ''}
          ${sb.medium > 0 ? `<div style="width: ${(sb.medium / webAssessment.totalFindings) * 100}%; background: ${sevColors.medium};" title="Medium: ${sb.medium}"></div>` : ''}
          ${sb.low > 0 ? `<div style="width: ${(sb.low / webAssessment.totalFindings) * 100}%; background: ${sevColors.low};" title="Low: ${sb.low}"></div>` : ''}
          ${sb.info > 0 ? `<div style="width: ${(sb.info / webAssessment.totalFindings) * 100}%; background: ${sevColors.info};" title="Info: ${sb.info}"></div>` : ''}
        </div>
      </div>`;

    // Findings table
    if (webAssessment.findings.length > 0) {
      html += `
      <div class="subsection">
        <h3>Web Findings</h3>
        <table style="width: 100%; border-collapse: collapse; margin-top: 1rem;">
          <thead>
            <tr style="background: #0d9488; color: white;">
              <th style="padding: 0.75rem; text-align: left;">Title</th>
              <th style="padding: 0.75rem; text-align: center;">Severity</th>
              <th style="padding: 0.75rem; text-align: center;">Confidence</th>
              <th style="padding: 0.75rem; text-align: left;">WSTG Test</th>
              <th style="padding: 0.75rem; text-align: left;">Endpoint</th>
            </tr>
          </thead>
          <tbody>
            ${webAssessment.findings.map((f, i) => `
              <tr style="background: ${i % 2 === 0 ? '#f0fdfa' : 'white'}; border-bottom: 1px solid #e5e7eb;">
                <td style="padding: 0.6rem 0.75rem;">${f.title}</td>
                <td style="padding: 0.6rem 0.75rem; text-align: center;">
                  <span style="background: ${sevColors[f.severity] || sevColors.info}; color: white; padding: 0.2rem 0.6rem; border-radius: 4px; font-size: 0.8rem; font-weight: 600;">${(f.severity || 'info').toUpperCase()}</span>
                </td>
                <td style="padding: 0.6rem 0.75rem; text-align: center;">${f.confidence || '-'}</td>
                <td style="padding: 0.6rem 0.75rem; font-family: monospace; font-size: 0.85rem;">${f.wstgTestId || '-'}</td>
                <td style="padding: 0.6rem 0.75rem; font-family: monospace; font-size: 0.85rem; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${f.endpoint || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>`;
    }

    // MITRE Cross-Reference
    if (webAssessment.mitreCrossReference && webAssessment.mitreCrossReference.length > 0) {
      html += `
      <div class="subsection">
        <h3>MITRE ATT&CK Cross-Reference</h3>
        <p style="color: #6b7280; margin-bottom: 1rem;">Web findings mapped to MITRE ATT&CK techniques:</p>
        <div style="display: flex; flex-wrap: wrap; gap: 0.5rem;">
          ${webAssessment.mitreCrossReference.map(m => `
            <div style="background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 6px; padding: 0.4rem 0.8rem; font-size: 0.85rem;">
              <strong style="color: #0369a1;">${m.techniqueId}</strong>
              <span style="color: #6b7280; margin-left: 0.3rem;">via ${m.wstgTestId || 'web test'}</span>
              <span style="background: ${sevColors[m.severity] || sevColors.info}; color: white; padding: 0.1rem 0.4rem; border-radius: 3px; font-size: 0.75rem; margin-left: 0.3rem;">${(m.severity || '').toUpperCase()}</span>
            </div>
          `).join('')}
        </div>
      </div>`;
    }

    return html;
  },

  generateEnhancedAttackPathHTML(attackPath) {
    if (!attackPath) return '<p>No attack path data available.</p>';
    
    return `
      <div class="attack-flow">
        <h3>Attack Chain Visualization</h3>
        <div style="background: #f3f4f6; padding: 1rem; border-radius: 8px; margin-bottom: 2rem;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div>
              <h4 style="margin: 0;">Attack Chain Completeness</h4>
              <p style="margin: 0.5rem 0 0 0; color: #6b7280;">
                ${attackPath.executedPhases} of ${attackPath.totalPhases} phases executed
              </p>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 2.5rem; font-weight: bold; color: ${
                attackPath.completeness >= 80 ? '#dc2626' :
                attackPath.completeness >= 60 ? '#ea580c' :
                attackPath.completeness >= 40 ? '#f59e0b' :
                '#10b981'
              };">${attackPath.completeness}%</div>
              <div style="font-size: 0.875rem; color: #6b7280;">Completion</div>
            </div>
          </div>
          <div style="margin-top: 1rem; background: #e5e7eb; border-radius: 9999px; height: 1rem; overflow: hidden;">
            <div style="background: ${
              attackPath.completeness >= 80 ? '#dc2626' :
              attackPath.completeness >= 60 ? '#ea580c' :
              attackPath.completeness >= 40 ? '#f59e0b' :
              '#10b981'
            }; height: 100%; width: ${attackPath.completeness}%; transition: width 0.3s ease;"></div>
          </div>
        </div>

        <h4>Attack Flow Diagram</h4>
        <div style="position: relative; padding: 2rem 0;">
          ${attackPath.phases.map((phase, index) => {
            const isLast = index === attackPath.phases.length - 1;
            const isCritical = phase.successfulCount > phase.actionCount * 0.7;
            
            return `
              <div style="display: flex; align-items: center; margin-bottom: ${isLast ? '0' : '2rem'};">
                <div class="flow-node ${isCritical ? 'critical' : ''}" style="flex: 1;">
                  <h5 style="margin: 0 0 0.5rem 0; color: ${isCritical ? '#dc2626' : '#1f2937'};">
                    ${index + 1}. ${phase.name}
                  </h5>
                  <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; margin-bottom: 0.5rem;">
                    <div style="font-size: 0.875rem;">
                      <strong>Actions:</strong> ${phase.actionCount}
                    </div>
                    <div style="font-size: 0.875rem;">
                      <strong>Successful:</strong> ${phase.successfulCount}
                    </div>
                  </div>
                  ${phase.keyActions.length > 0 ? `
                    <div style="font-size: 0.875rem; color: #6b7280;">
                      <strong>Key Actions:</strong>
                      <ul style="margin: 0.25rem 0 0 1rem; padding: 0;">
                        ${phase.keyActions.map(action => `<li>${action}</li>`).join('')}
                      </ul>
                    </div>
                  ` : ''}
                </div>
                ${!isLast ? '<div class="flow-arrow" style="margin: 0 1rem;"></div>' : ''}
              </div>
            `;
          }).join('')}
        </div>

        ${attackPath.criticalPaths && attackPath.criticalPaths.length > 0 ? `
          <div style="margin-top: 2rem;">
            <h4>Critical Attack Paths Identified</h4>
            ${attackPath.criticalPaths.map(path => `
              <div style="background: ${
                path.severity === 'critical' ? '#fee2e2' : '#fed7aa'
              }; border: 2px solid ${
                path.severity === 'critical' ? '#dc2626' : '#ea580c'
              }; padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
                <h5 style="margin: 0 0 0.5rem 0; color: ${
                  path.severity === 'critical' ? '#dc2626' : '#ea580c'
                };">${path.name}</h5>
                <p style="margin: 0;">${path.description}</p>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `;
  },

  generateDetailedActionsHTML(detailedActions) {
    if (!detailedActions || detailedActions.length === 0) return '<p>No detailed actions available.</p>';
    
    const severityColors = {
      critical: '#7c3aed', // purple
      high: '#dc2626',     // red
      medium: '#ea580c',   // orange
      low: '#ca8a04'       // dark yellow
    };
    
    return detailedActions.map(action => `
      <div class="action-detail" style="margin-bottom: 2rem; padding: 1.5rem; background: #f9fafb; border: 2px solid ${severityColors[action.severity] || '#e5e7eb'};">
        <div style="display: flex; justify-content: space-between; align-items: start;">
          <h3 style="margin: 0; color: #1f2937;">${action.title}</h3>
          <span style="background: ${severityColors[action.severity] || '#6b7280'}; color: white; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.875rem; font-weight: 600;">
            ${action.severity ? action.severity.toUpperCase() : 'UNKNOWN'}
          </span>
        </div>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin: 1rem 0;">
          <div><strong>Time:</strong> ${new Date(action.timestamp).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
          <div><strong>Operator:</strong> ${action.operator}</div>
          <div><strong>Result:</strong> <span class="badge ${action.result}" style="background: ${
            action.result === 'successful' ? '#d1fae5' :
            action.result === 'detected' ? '#fed7aa' :
            action.result === 'blocked' ? '#fee2e2' :
            '#f3f4f6'
          }; color: ${
            action.result === 'successful' ? '#065f46' :
            action.result === 'detected' ? '#92400e' :
            action.result === 'blocked' ? '#991b1b' :
            '#1f2937'
          }; padding: 0.25rem 0.5rem; border-radius: 4px;">${action.result}</span></div>
          <div><strong>Duration:</strong> ${action.duration || 'N/A'}</div>
          <div><strong>Technique:</strong> ${action.technique || 'N/A'}</div>
        </div>
        <div style="margin: 1rem 0;">
          <strong>Description:</strong>
          <p style="margin-top: 0.5rem; color: #4b5563;">${action.description}</p>
        </div>
        ${action.tactic ? `
          <div style="margin: 1rem 0;">
            <strong>MITRE ATT&CK Tactic:</strong> <span style="color: #7c3aed;">${action.tactic}</span>
          </div>
        ` : ''}
        ${action.targets && action.targets.length > 0 ? `
          <div style="margin: 1rem 0;">
            <strong>Affected Targets:</strong>
            <div style="display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.5rem;">
              ${action.targets.map(target => `
                <span style="background: ${target.type === 'ip' ? '#dbeafe' : '#d1fae5'}; color: ${target.type === 'ip' ? '#1e40af' : '#065f46'}; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.875rem;">
                  ${target.type === 'ip' ? '🌐' : '🖥️'} ${target.value}
                </span>
              `).join('')}
            </div>
          </div>
        ` : ''}
        ${action.notes ? `
          <div style="margin: 1rem 0;">
            <strong>Notes:</strong>
            <pre style="background: white; padding: 1rem; border-radius: 4px; overflow-x: auto; margin-top: 0.5rem; white-space: pre-wrap;">${action.notes}</pre>
          </div>
        ` : ''}
        ${action.evidence && action.evidence.length > 0 ? `
          <div class="evidence-container" style="margin-top: 1rem;">
            <strong>Evidence:</strong>
            ${action.evidence.map((evidence, i) => {
              if (evidence.type === 'screenshot' && evidence.data) {
                return `
                  <div style="margin: 1rem 0;">
                    <img src="${evidence.data}" alt="Evidence ${i + 1}" class="evidence-screenshot" style="max-width: 100%; height: auto; border: 1px solid #e5e7eb; border-radius: 4px;" />
                    <p style="text-align: center; color: #6b7280; margin-top: 0.5rem;">${evidence.caption || `Screenshot ${i + 1}`}</p>
                  </div>
                `;
              } else if (evidence.type === 'url') {
                return `<p>Evidence URL: <a href="${evidence.url}" target="_blank" style="color: #2563eb;">${evidence.url}</a></p>`;
              }
              return '';
            }).join('')}
          </div>
        ` : ''}
        ${action.tags && action.tags.length > 0 ? `
          <div style="margin-top: 1rem;">
            <strong>Tags:</strong>
            ${action.tags.map(tag => `<span style="background: #e5e7eb; padding: 0.25rem 0.5rem; border-radius: 4px; margin-left: 0.5rem; font-size: 0.875rem;">${tag}</span>`).join('')}
          </div>
        ` : ''}
      </div>
    `).join('');
  },

  generateRecommendationsHTML(recommendations) {
    if (!recommendations || !Array.isArray(recommendations) || recommendations.length === 0) {
      return '<p>No recommendations available.</p>';
    }
    const priorityOrder = { immediate: 0, high: 1, medium: 2, low: 3 };
    const sortedRecs = [...recommendations].sort((a, b) =>
      (priorityOrder[a.priority] ?? 99) - (priorityOrder[b.priority] ?? 99)
    );

    return sortedRecs.map(rec => `
      <div class="recommendation">
        <h3>${rec.title || 'Recommendation'}</h3>
        <p><strong>Priority:</strong> ${(rec.priority || 'medium').toUpperCase()} | <strong>Effort:</strong> ${rec.effort || 'N/A'} | <strong>Impact:</strong> ${rec.impact || 'N/A'}</p>
        <p>${rec.description || ''}</p>
      </div>
    `).join('');
  },

  createEnhancedTimelineTable(timeline) {
    const rows = [
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: '#', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: 'Start Time', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: 'End Time', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: 'Action', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: 'Technique', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: 'Operator', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: 'Result', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: 'Duration', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: 'Critical', bold: true })] })
        ]
      }),
      ...timeline.map(event => 
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ text: String(event.sequence) })] }),
            new TableCell({ children: [new Paragraph({ text: new Date(event.timestamp).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) })] }),
            new TableCell({ children: [new Paragraph({ text: event.endTime ? new Date(event.endTime).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A' })] }),
            new TableCell({ children: [new Paragraph({ text: event.action })] }),
            new TableCell({ children: [new Paragraph({ text: event.technique })] }),
            new TableCell({ children: [new Paragraph({ text: event.operator })] }),
            new TableCell({ children: [new Paragraph({ text: event.result })] }),
            new TableCell({ children: [new Paragraph({ text: event.duration })] }),
            new TableCell({ children: [new Paragraph({ text: event.critical ? 'Yes' : 'No' })] })
          ]
        })
      )
    ];

    return new Table({
      rows,
      width: { size: 100, type: WidthType.PERCENTAGE }
    });
  },

  formatKey(key) {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  },

  // Generate severity distribution bar chart
  generateSeverityBarChart(findings) {
    const critical = findings.critical ? findings.critical.length : 0;
    const high = findings.high ? findings.high.length : 0;
    const medium = findings.medium ? findings.medium.length : 0;
    const low = findings.low ? findings.low.length : 0;
    
    const total = critical + high + medium + low;
    const maxCount = Math.max(critical, high, medium, low, 1);
    
    const barHeight = 30;
    const chartHeight = 150;
    const chartWidth = 400;
    
    if (total === 0) {
      return '<p style="color: #6b7280;">No findings to display</p>';
    }
    
    return `
      <svg width="${chartWidth}" height="${chartHeight}" style="border: 1px solid #e5e7eb; background: #f9fafb;">
        <!-- Critical -->
        <rect x="10" y="10" width="${(critical / maxCount) * 350}" height="${barHeight}" fill="#7c3aed" />
        <text x="15" y="28" fill="white" font-size="14" font-weight="bold">${critical} Critical</text>
        
        <!-- High -->
        <rect x="10" y="45" width="${(high / maxCount) * 350}" height="${barHeight}" fill="#dc2626" />
        <text x="15" y="63" fill="white" font-size="14" font-weight="bold">${high} High</text>
        
        <!-- Medium -->
        <rect x="10" y="80" width="${(medium / maxCount) * 350}" height="${barHeight}" fill="#ea580c" />
        <text x="15" y="98" fill="white" font-size="14" font-weight="bold">${medium} Medium</text>
        
        <!-- Low -->
        <rect x="10" y="115" width="${(low / maxCount) * 350}" height="${barHeight}" fill="#ca8a04" />
        <text x="15" y="133" fill="white" font-size="14" font-weight="bold">${low} Low</text>
      </svg>
      <div style="margin-top: 0.5rem; text-align: center; color: #6b7280; font-size: 0.875rem;">
        Total Findings: ${total}
      </div>
    `;
  },

  // Generate target results chart
  generateTargetResultsChart(actionsByTarget) {
    const targets = Object.entries(actionsByTarget).filter(([_, data]) =>
      data.actions && data.actions.length > 0
    ).slice(0, 10);

    if (targets.length === 0) {
      const targetCount = Object.keys(actionsByTarget).length;
      return `
        <div style="background: #f3f4f6; padding: 2rem; border-radius: 8px; text-align: center;">
          <p style="color: #6b7280; margin-bottom: 1rem;">No actions are currently linked to targets.</p>
          <p style="color: #9ca3af; font-size: 0.875rem;">
            ${targetCount} target(s) defined, but actions may not have target associations.
          </p>
        </div>
      `;
    }

    const chartHeight = targets.length * 40 + 40;
    const chartWidth = 600;
    const barHeight = 25;
    const maxActions = Math.max(...targets.map(([_, data]) => data.actions ? data.actions.length : 0), 1);

    const targetBars = targets.map(([targetKey, data], index) => {
      const y = index * 40 + 20;
      const segments = [
        { count: data.successful || 0, color: '#10b981' },
        { count: data.detected || 0, color: '#f59e0b' },
        { count: data.blocked || 0, color: '#ef4444' },
        { count: data.failed || 0, color: '#6b7280' },
        { count: data.partial || 0, color: '#3b82f6' },
        { count: data.unknown || 0, color: '#8b5cf6' }
      ];
      const total = data.actions ? data.actions.length : 0;
      const displayName = data.value || targetKey;
      const truncatedName = displayName.length > 25 ? displayName.substring(0, 22) + '...' : displayName;

      let xOffset = 0;
      const bars = segments.filter(s => s.count > 0).map(seg => {
        const w = (seg.count / maxActions) * 350;
        const bar = `<rect x="${xOffset}" y="0" width="${w}" height="${barHeight}" fill="${seg.color}" /><text x="${xOffset + w/2}" y="17" text-anchor="middle" fill="white" font-size="12">${seg.count}</text>`;
        xOffset += w;
        return bar;
      }).join('');

      return `<text x="10" y="${y + 17}" font-size="12" fill="#374151">${truncatedName}</text><g transform="translate(200, ${y})">${bars}<text x="${(total / maxActions) * 350 + 10}" y="17" font-size="12" fill="#374151" font-weight="bold">${total}</text></g>`;
    }).join('');

    return `
      <svg width="${chartWidth}" height="${chartHeight}" style="border: 1px solid #e5e7eb; background: #f9fafb;">
        ${targetBars}
        <g transform="translate(200, ${chartHeight - 20})">
          <rect x="0" y="0" width="15" height="15" fill="#10b981" /><text x="20" y="12" font-size="11" fill="#374151">Success</text>
          <rect x="70" y="0" width="15" height="15" fill="#f59e0b" /><text x="90" y="12" font-size="11" fill="#374151">Detected</text>
          <rect x="150" y="0" width="15" height="15" fill="#ef4444" /><text x="170" y="12" font-size="11" fill="#374151">Blocked</text>
          <rect x="220" y="0" width="15" height="15" fill="#6b7280" /><text x="240" y="12" font-size="11" fill="#374151">Failed</text>
          <rect x="280" y="0" width="15" height="15" fill="#3b82f6" /><text x="300" y="12" font-size="11" fill="#374151">Partial</text>
          <rect x="350" y="0" width="15" height="15" fill="#8b5cf6" /><text x="370" y="12" font-size="11" fill="#374151">Unknown</text>
        </g>
      </svg>
    `;
  }
};

export default debriefExportService;